/**
 * Code-splitting adapter — modern implementation (React.lazy, Vite brands). Same API as `./index.tsx`.
 */
import React, {createContext, Suspense, useContext, useState} from 'react';

import useShellGate from 'coreHooks/useShellGate';

// React 19 API (core `tsc` uses the React 18 types, see PLAN.md "Dove si può usare React 19").
const {use} = React as unknown as {use: <T, >(promise: Promise<T>) => T};

type LoadedModule<P> = {default: React.ComponentType<P>};
type Importer<P> = (() => Promise<LoadedModule<P>>) & {moduleId?: string};

export type LazyComponent<P> = React.ComponentType<P> & {load: () => Promise<unknown>; preload: () => void};

/**
 * Per-request set of the lazy modules rendered on the server (ids set by the `thp:lazy-module-ids`
 * Vite plugin), used by `entry.server` to emit `<link rel="modulepreload">` and to tell `entry.client`
 * which modules to load before hydrating. Not provided on the client.
 */
export const LazyModuleCollectorContext = createContext<Set<string> | null>(null);

/** Loaders of the lazy modules defined so far, by module id (filled as chunks are evaluated). */
const loadersById = new Map<string, () => Promise<unknown>>();

const createLoader = <P, >(importer: Importer<P>) => {
	let promise: Promise<LoadedModule<P>> | null = null;
	let loaded: LoadedModule<P> | null = null;

	const load = () => {
		if (!promise) {
			promise = importer().then(
				(module) => {
					loaded = module;

					return module;
				},
				(error) => {
					promise = null;
					throw error;
				},
			);
		}

		return promise;
	};

	if (importer.moduleId) {
		loadersById.set(importer.moduleId, load);
	}

	return {load, getLoaded: () => loaded};
};

/**
 * Loads the given lazy modules, including the ones defined by the chunks loaded along the way
 * (routes → `APIRouter` → page). Called before hydration with the modules rendered on the server:
 * an already loaded module renders synchronously, so its server HTML is hydrated instead of being
 * discarded and rendered again (a dehydrated Suspense boundary that receives an update before its
 * lazy chunk is loaded is client-rendered by React).
 */
export const preloadLazyModules = async (moduleIds: string[]): Promise<void> => {
	const pending = new Set(moduleIds);
	let loading: Promise<unknown>[];

	do {
		loading = Array.from(pending)
			.filter((id) => loadersById.has(id))
			.map((id) => {
				pending.delete(id);

				return loadersById.get(id)().catch((error) => console.error(`[preloadLazyModules] ${id}`, error));
			});

		await Promise.all(loading);
	} while (loading.length > 0 && pending.size > 0);
};

const withLoadMethods = <P, >(Component: React.ComponentType<P>, load: () => Promise<unknown>) => {
	const Lazy = Component as LazyComponent<P>;

	Lazy.load = load;

	Lazy.preload = () => {
		load();
	};

	return Lazy;
};

const useCollectModule = (moduleId?: string) => {
	const collector = useContext(LazyModuleCollectorContext);

	if (collector && moduleId) {
		collector.add(moduleId);
	}
};

/**
 * Server, module not loaded yet: same place as the `React.lazy` element, but it suspends itself on the
 * load registered in the shell gate (`coreUtils/ssrShell`). Fizz retries this component once the module
 * is loaded, so the shell waits until the module is rendered, not only loaded.
 */
const createServerLazy = <P, >(load: () => Promise<LoadedModule<P>>, moduleId = 'anonymous') => {
	const ServerLazy = (props: P) => {
		const gate = useShellGate();
		const gateId = `lazy:${moduleId}`;

		gate?.begin(gateId);

		const {default: Component} = use(load());

		gate?.end(gateId);

		return <Component {...(props as P & React.Attributes)} />;
	};

	return ServerLazy;
};

/**
 * Renders the loaded component directly when its module is already loaded, `React.lazy` otherwise
 * (which always suspends once; on the server a component that holds the shell until it is rendered).
 * Chosen once per instance: switching type would remount the subtree.
 */
const useLazyComponent = <P, >(
	Lazy: React.ComponentType<P>,
	ServerLazy: React.ComponentType<P>,
	getLoaded: () => LoadedModule<P> | null,
) => {
	const [Component] = useState(() => getLoaded()?.default ?? (typeof window === 'undefined' ? ServerLazy : Lazy));

	return Component;
};

export const lazyComponent = <P extends object = Record<string, unknown>, >(
	importer: Importer<P>,
	{fallback = null}: {fallback?: React.ReactElement | null} = {},
) => {
	const {load, getLoaded} = createLoader(importer);
	const Lazy = React.lazy(load) as unknown as React.ComponentType<P>;
	const ServerLazy = createServerLazy(load, importer.moduleId);

	const LazyWithFallback = (props: P) => {
		useCollectModule(importer.moduleId);

		const Component = useLazyComponent(Lazy, ServerLazy, getLoaded);

		return (
			<Suspense fallback={fallback}>
				<Component {...(props as P & React.Attributes)} />
			</Suspense>
		);
	};

	return withLoadMethods<P>(LazyWithFallback, load);
};

export const suspenseComponent = <P extends object = Record<string, unknown>, >(
	importer: Importer<P>,
	_options: {fallback?: React.ReactElement} = {},
) => {
	const {load, getLoaded} = createLoader(importer);
	const Lazy = React.lazy(load) as unknown as React.ComponentType<P>;
	const ServerLazy = createServerLazy(load, importer.moduleId);

	const SuspenseLazy = (props: P) => {
		useCollectModule(importer.moduleId);

		const Component = useLazyComponent(Lazy, ServerLazy, getLoaded);

		return <Component {...(props as P & React.Attributes)} />;
	};

	return withLoadMethods<P>(SuspenseLazy, load);
};
